import React, { useState } from 'react';
import './DateRangePicker.css';

const DateRangePicker = ({ startDate, endDate, onDateChange, onClose }) => {
  const [currentMonth, setCurrentMonth] = useState(() => {
    const date = startDate ? new Date(startDate) : new Date('2026-01-01');
    return new Date(date.getFullYear(), date.getMonth(), 1);
  });

  const [selectedStart, setSelectedStart] = useState(startDate || '');
  const [selectedEnd, setSelectedEnd] = useState(endDate || '');

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const daysOfWeek = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const isDateInRange = (dateStr) => {
    if (!selectedStart || !selectedEnd) return false;
    return dateStr >= selectedStart && dateStr <= selectedEnd;
  };

  const isDateSelected = (dateStr) => {
    return dateStr === selectedStart || dateStr === selectedEnd;
  };

  const handleDateClick = (dateStr) => {
    if (!selectedStart || (selectedStart && selectedEnd)) {
      // Start new selection
      setSelectedStart(dateStr);
      setSelectedEnd('');
    } else if (selectedStart && !selectedEnd) {
      // Complete the range
      if (dateStr < selectedStart) {
        // If clicked date is before start, swap them
        setSelectedEnd(selectedStart);
        setSelectedStart(dateStr);
      } else {
        setSelectedEnd(dateStr);
      }
    }
  };

  const handleApply = () => {
    onDateChange(selectedStart, selectedEnd);
    if (onClose) onClose();
  };

  const handleClear = () => {
    setSelectedStart('');
    setSelectedEnd('');
    onDateChange('', '');
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const renderCalendar = (monthOffset = 0) => {
    const displayMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + monthOffset, 1);
    const daysInMonth = getDaysInMonth(displayMonth);
    const firstDay = getFirstDayOfMonth(displayMonth);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to start of day for accurate comparison
    const todayStr = formatDate(today);

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(displayMonth.getFullYear(), displayMonth.getMonth(), day);
      const dateStr = formatDate(date);
      days.push({ day, dateStr, date });
    }

    return (
      <div className="calendar-month">
        <div className="calendar-header">
          <button 
            className="calendar-nav-btn" 
            onClick={monthOffset === 0 ? prevMonth : () => {}}
            disabled={monthOffset === 1}
          >
            ‹
          </button>
          <div className="calendar-month-year">
            {months[displayMonth.getMonth()]} {displayMonth.getFullYear()}
          </div>
          <button 
            className="calendar-nav-btn" 
            onClick={monthOffset === 1 ? nextMonth : () => {}}
            disabled={monthOffset === 0}
          >
            ›
          </button>
        </div>
        
        <div className="calendar-days-header">
          {daysOfWeek.map(day => (
            <div key={day} className="calendar-day-header">{day}</div>
          ))}
        </div>
        
        <div className="calendar-days">
          {days.map((dayData, index) => {
            if (!dayData) {
              return <div key={`empty-${index}`} className="calendar-day empty"></div>;
            }

            const { day, dateStr, date } = dayData;
            const dateObj = new Date(date);
            dateObj.setHours(0, 0, 0, 0);
            const isFuture = dateObj > today;
            const isPast = dateStr < todayStr;
            const isToday = dateStr === todayStr;
            const inRange = isDateInRange(dateStr);
            const isSelected = isDateSelected(dateStr);
            const isStart = dateStr === selectedStart;
            const isEnd = dateStr === selectedEnd;
            const isSelectable = !isFuture; // Allow today and past dates

            return (
              <div
                key={dateStr}
                className={`calendar-day ${isPast ? 'past' : ''} ${isToday ? 'today' : ''} ${isFuture ? 'future' : ''} ${inRange ? 'in-range' : ''} ${isSelected ? 'selected' : ''} ${isStart ? 'start' : ''} ${isEnd ? 'end' : ''}`}
                onClick={() => isSelectable && handleDateClick(dateStr)}
              >
                <span className="day-number">{day}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="date-range-picker-overlay" onClick={onClose}>
      <div className="date-range-picker" onClick={(e) => e.stopPropagation()}>
        <div className="date-range-picker-header">
          <div className="selected-range-display">
            {selectedStart && selectedEnd ? (
              <span>{selectedStart} → {selectedEnd}</span>
            ) : selectedStart ? (
              <span>{selectedStart} → Select end date</span>
            ) : (
              <span>Select date range</span>
            )}
          </div>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        
        <div className="calendars-container">
          {renderCalendar(0)}
          {renderCalendar(1)}
        </div>
        
        <div className="date-range-picker-footer">
          <button className="clear-btn" onClick={handleClear}>Clear</button>
          <button className="apply-btn" onClick={handleApply}>Apply</button>
        </div>
      </div>
    </div>
  );
};

export default DateRangePicker;
